import { useDebounce } from "@api";
import { Button, Stack, Typography } from "@mui/material";
import type { StackProps } from "@mui/material";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { TextClamperTypography, classes } from "./styles";

interface ITextClamperProps {
  text: ReactNode;
  maxLine?: number;
  compact?: boolean;
  label?: string;
  clampLabel?: {
    open: string;
    close: string;
  };
  stackProps?: StackProps;
}

export const TextClamper = ({
  text,
  maxLine = 1,
  compact = false,
  label,
  clampLabel = {
    open: "Afficher plus...",
    close: "Afficher moins",
  },
  stackProps,
}: ITextClamperProps) => {
  const [clamped, setClamped] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const containerRef = useRef<HTMLParagraphElement>(null);

  const isClamping = (el: HTMLParagraphElement) => {
    const { clientHeight, scrollHeight } = el;
    return clientHeight !== scrollHeight;
  };

  const handleDebounce = useDebounce(
    () => {
      if (containerRef.current) {
        const hadClampClass = containerRef.current.classList.contains(classes.clamp);
        if (!hadClampClass) {
          containerRef.current.classList.add(classes.clamp);
        }
        setShowButton(isClamping(containerRef.current));
        if (!hadClampClass) {
          containerRef.current.classList.remove(classes.clamp);
        }
      }
    },
    50,
    [containerRef]
  );

  useEffect(() => {
    handleDebounce();
    window.addEventListener("resize", handleDebounce);

    return () => {
      window.removeEventListener("resize", handleDebounce);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, text]);

  return (
    <Stack
      {...stackProps}
      sx={[
        {
          gap: 1,
          alignItems: "flex-start",
          flexDirection: clamped && compact ? "row" : "column",
        },
        ...(Array.isArray(stackProps?.sx) ? stackProps.sx : [stackProps?.sx]),
      ]}
    >
      <TextClamperTypography
        ref={containerRef}
        className={clamped ? classes.clamp : undefined}
        color="black.main"
        maxLine={maxLine}
        paddingTop={compact ? 1 / 4 : 0}
      >
        <b>{label}</b>
        {text}
      </TextClamperTypography>
      {showButton && (
        <Button
          variant="text"
          onClick={() => setClamped(!clamped)}
          sx={{
            padding: 0,
            "&.MuiButtonBase-root:hover": {
              bgcolor: "transparent",
            },
            minWidth: "max-content",
          }}
          disableRipple
        >
          <Typography variant="body1SemiBold" sx={{ textTransform: "none" }}>
            {clamped ? clampLabel.open : clampLabel.close}
          </Typography>
        </Button>
      )}
    </Stack>
  );
};
